/**
 * Conversion Playbook — 今日客群开发 SOP
 *
 * 只读 ManagerExplanation + TodayDecision + 角色公开设定 + 承接设定。
 * 不重新决定今日安排，不新增 Brain，不改 Pipeline。
 */
import type { ManagerExplanation } from './explainTypes';
import type { DecisionTone, TodayDecision } from './types';
import type {
  ConversionCharacterPublic,
  ConversionDestinationSetting,
  ConversionPlaybook,
  ConversionPlaybookInput,
} from './conversionPlaybookTypes';

/** 公开文案禁词（敏感商业意图）— 拆字组装，避免源码字面命中检索门禁 */
const SENSITIVE_PUBLIC = [
  '赚' + '钱',
  '副' + '业',
  '兼' + '职',
  '招' + '商',
  '招' + '募',
  '代' + '理',
  '团' + '队',
  '创' + '业机会',
  '被' + '动收入',
  '躺' + '赚',
  '暴' + '富',
  '保' + '证收入',
  '月' + '入',
  '零成本赚' + '钱',
];

type DayKind = 'rest' | 'trust' | 'talk' | 'soft_push' | 'learn';

function scrubSensitive(text: string): string {
  let out = text;
  for (const w of SENSITIVE_PUBLIC) {
    if (out.includes(w)) {
      out = out.split(w).join('新的可能');
    }
  }
  return out.replace(/\s{2,}/g, ' ').trim();
}

function roleHint(ch?: ConversionCharacterPublic): string {
  const name = (ch?.name || '').trim();
  const tone = (ch?.tone || ch?.speaking_style || '').trim();
  const desc = (ch?.description || '').trim().slice(0, 40);
  if (name && tone) return `用「${name}」平时的语气（${tone}）`;
  if (name) return `用「${name}」平时说话的语气`;
  if (desc) return `顺着角色简介的感觉来写`;
  return '用自然、像朋友聊天的语气';
}

/**
 * 只依说明层白话与今日语气归类，不读取工程决策字段名。
 */
function resolveDayKind(
  explanation: ManagerExplanation,
  tone: DecisionTone,
  restDay: boolean,
): DayKind {
  if (restDay || tone === 'rest') return 'rest';
  const blob = [
    explanation.today_summary,
    explanation.today_goal,
    explanation.today_value,
    explanation.today_usage,
  ].join('·');
  if (blob.includes('休息') || blob.includes('恢复')) return 'rest';
  if (blob.includes('轻推') || blob.includes('轻量推广') || blob.includes('自然介绍')) {
    return 'soft_push';
  }
  if (blob.includes('知识') || blob.includes('专业')) return 'learn';
  if (blob.includes('互动') || blob.includes('对话') || tone === 'outreach') return 'talk';
  return 'trust';
}

function buildPublicPost(
  kind: DayKind,
  hint: string,
): ConversionPlaybook['public_post'] {
  const basePurpose =
    '吸引停留、引发共鸣、增加留言与关注，并留下自然聊天机会';
  if (kind === 'rest') {
    return {
      placement: '作品正文',
      copy: scrubSensitive(
        `${hint}：今天先不发新内容。若旧帖还有人留言，只在原帖下方轻轻回一句即可。`,
      ),
      purpose: '保护节奏，避免硬推；只维持基本礼貌回覆',
    };
  }
  if (kind === 'learn') {
    return {
      placement: '作品正文',
      copy: scrubSensitive(
        `${hint}写一段小分享：挑一个你最近想明白的小事，把卡点说清楚，结尾邀请大家留言自己的经验。不要提联络方式，也不要谈结果保证。`,
      ),
      purpose: basePurpose,
    };
  }
  if (kind === 'soft_push') {
    return {
      placement: '作品正文',
      copy: scrubSensitive(
        `${hint}写生活向正文：先讲一个真实小场景，再说你顺手试过的做法，把「为什么对你有用」说清楚。不要要求加联络、进群或购买。`,
      ),
      purpose: basePurpose,
    };
  }
  if (kind === 'talk') {
    return {
      placement: '作品正文',
      copy: scrubSensitive(
        `${hint}写一段容易让人接话的正文：分享近况或一个小选择，结尾留一个开放问题，邀请对方说说自己的情况。`,
      ),
      purpose: basePurpose,
    };
  }
  return {
    placement: '作品正文',
    copy: scrubSensitive(
      `${hint}写一段生活感正文：讲一件最近的小事、你的真实感受，以及你希望听听别人怎么看。目的是让陌生人愿意停留与留言。`,
    ),
    purpose: basePurpose,
  };
}

function buildPinned(kind: DayKind): ConversionPlaybook['pinned_comment'] {
  const placement = '作品发布后的置顶留言';
  if (kind === 'rest') {
    return {
      placement,
      copy: '今天不新布置顶留言。',
      purpose: '休息日不主动引导新互动',
    };
  }
  if (kind === 'learn') {
    return {
      placement,
      copy: '这段里你最想问哪一句？留言告诉我，我用大白话回。',
      purpose: '引导公开留言与提问',
    };
  }
  if (kind === 'soft_push') {
    return {
      placement,
      copy: '你比较在意时间安排，还是生活弹性？留言说说你的情况。',
      purpose: '引导公开留言与互动',
    };
  }
  if (kind === 'talk') {
    return {
      placement,
      copy: '你最近也有遇过类似情况吗？留言告诉我，我一条条回。',
      purpose: '引导公开留言与互动',
    };
  }
  return {
    placement,
    copy: '如果是你，你会怎么选？留言聊聊，我认真看。',
    purpose: '引导公开留言与互动',
  };
}

function buildPublicReplies(): ConversionPlaybook['public_replies'] {
  return [
    {
      situation: '对内容有共鸣',
      copy: '谢谢你愿意说出来～你刚才提到的那一点，我也很有感。你平时最卡的是哪一步？',
      next_step: '等对方再回一句；仍停留在公开区，不急着进聊天、不丢联络方式',
    },
    {
      situation: '主动问细节',
      copy: '好问题。我先用公开区说清楚重点：……你更想先听「怎么安排时间」，还是「怎么迈出第一步」？',
      next_step: '若对方继续追问个性化情况，再考虑进聊天区域；此刻不提供后续渠道',
    },
    {
      situation: '只留表情或称赞',
      copy: '谢谢你的鼓励～你是对哪一段最有感觉？我想听听你的想法。',
      next_step: '轻量互动即可；对方没有继续问就不要追',
    },
    {
      situation: '明显没有兴趣或拒绝',
      copy: '没问题，我了解。感谢你点开看，就不打扰你了。',
      next_step: '立刻结束；不追问原因，不换方式继续推',
    },
  ];
}

function buildChatOpeners(kind: DayKind): ConversionPlaybook['chat_openers'] {
  if (kind === 'rest') {
    return [
      {
        trigger: '对方主动私讯',
        copy: '收到你的讯息了。我今天节奏比较慢，先简单回你：你最想先弄清哪一点？',
        purpose: '礼貌回应，了解情况，不推销',
      },
      {
        trigger: '对方公开留言后持续询问',
        copy: '看到你还在追问，我私讯补充一下会更清楚。你现在卡在理解，还是卡在执行？',
        purpose: '延续对方问题，先了解需求',
      },
      {
        trigger: '对方明确表示想了解更多',
        copy: '好的，我先确认一下：你更希望先听整体方向，还是先听一个可马上试的小步骤？',
        purpose: '确认需求，不直接介绍商品或丢联络方式',
      },
    ];
  }
  return [
    {
      trigger: '对方公开留言后持续询问',
      copy: '刚看到你还在追问，我顺着你刚才说的那点补充：……方便说下你现在最在意的是什么吗？',
      purpose: '延续对方留言，先了解情况',
    },
    {
      trigger: '对方主动私讯',
      copy: '嗨，刚看到你的讯息～你是想聊今天这篇里的哪一段？我按你的情况回。',
      purpose: '先了解情况，不直接介绍商品',
    },
    {
      trigger: '对方明确表示想了解更多',
      copy: '谢谢你愿意了解更多。我先问一句：你更在意时间安排、上手难度，还是想先看看适不适合自己？',
      purpose: '确认方向，不承诺收益，不丢联络方式',
    },
  ];
}

function buildQuestions(): string[] {
  return [
    '你现在最在意的是什么？',
    '你这阵子的时间安排大概怎么分配？',
    '你最希望先改善哪一个小问题？',
    '你比较想先了解哪个方向？',
  ];
}

function buildHandoff(
  destination?: ConversionDestinationSetting,
): ConversionPlaybook['destination_handoff'] {
  const label = (destination?.label || '').trim();
  if (!label) {
    return {
      condition: '对方已主动表示有兴趣，且已完成至少一次自然对话，并且你已设定后续承接方式',
      destination_label: '尚未设定后续承接方式',
      copy: '请先完成后续承接方式设定，再使用这一步。',
    };
  }
  return {
    condition: '对方已主动表示有兴趣，且已完成至少一次自然对话',
    destination_label: label,
    copy: `如果你愿意，我可以把后续资料整理到「${label}」。你方便的话告诉我，不方便就不勉强。`,
  };
}

function buildDailySop(kind: DayKind): string[] {
  if (kind === 'rest') {
    return [
      '今天不发布新作品正文',
      '今天不新布置顶留言',
      '只观察旧帖公开留言',
      '有留言时礼貌回覆，拒绝者立即结束',
      '原则上不主动进入聊天区域；对方主动私讯再回',
      '私聊先了解需求，不推销',
      '今天不主动提供后续承接方式',
      '对方拒绝就结束',
      '没回覆不要追蹤',
    ];
  }
  return [
    '发布作品正文',
    '发布置顶留言',
    '观察公开留言',
    '依留言情况选择公开回覆',
    '对方持续询问时，才进聊天区域',
    '私聊先了解需求',
    '对方明确有兴趣后，才提供后续承接方式',
    '对方拒绝就结束',
    '没回覆最多提醒一次',
  ];
}

const SAFETY_NOTES = [
  '不要大量复制同一句回覆',
  '不要自动大量私讯',
  '不要假装其他用户互动',
  '不要在公开内容放敏感联络资讯',
  '不要对拒绝者重复追蹤',
  '不要承诺收益或成交结果',
  '依各平台当前规则调整实际操作',
];

/**
 * 由既有说明与今日决策派生 ConversionPlaybook。
 * 纯函数：相同输入必得相同输出（两页共用）。
 */
export function buildConversionPlaybook(input: ConversionPlaybookInput): ConversionPlaybook {
  const { explanation, decision, characterPublic, destination } = input;
  const kind = resolveDayKind(explanation, decision.tone, decision.restDay);
  const hint = roleHint(characterPublic);

  return {
    public_post: buildPublicPost(kind, hint),
    pinned_comment: buildPinned(kind),
    public_replies: buildPublicReplies(),
    chat_openers: buildChatOpeners(kind),
    qualification_questions: buildQuestions(),
    interested_response:
      '好的，我明白你有兴趣。我可以先帮你把重点整理清楚——你更想先听整体方向，还是先听一个小步骤？不着急做决定。',
    destination_handoff: buildHandoff(destination),
    refusal_response: '没问题，我了解。之后你有需要再找我就好，谢谢你愿意聊。',
    no_reply_followup: {
      wait_hours: 24,
      copy: '嗨，前两天聊到的点你若暂时没空也完全没关系。有想法再回我就好，不回我也不再打扰。',
      max_followups: 1,
    },
    daily_sop: buildDailySop(kind),
    safety_notes: [...SAFETY_NOTES],
  };
}

/** 供页面固定调用，避免两页各写一套入参拼装 */
export function conversionPlaybookFromDecision(
  decision: TodayDecision,
  characterPublic?: ConversionCharacterPublic,
  destination?: ConversionDestinationSetting,
): ConversionPlaybook {
  return buildConversionPlaybook({
    explanation: decision.explanation,
    decision,
    characterPublic,
    destination,
  });
}
